import React from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  HStack,
  Badge,
  Icon,
  useColorModeValue,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { 
  StarIcon, 
  ViewIcon, 
  InfoIcon,
  ArrowBackIcon,
  SettingsIcon
} from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  isContestProblem, 
  getContestProblemName, 
  getContestProblemShortName,
  getContestYear,
  getContestProblemTags,
  getContestLeaderboardUrl
} from '../utils/contestUtils';

interface ContestNavbarProps {
  problemId: string;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  isPolling?: boolean;
  onOpenRunSettings?: () => void;
  onRestoreToDefault?: () => void;
  hasUnsavedChanges?: boolean;
}

export default function ContestNavbar({ 
  problemId, 
  onSubmit, 
  isSubmitting = false, 
  isPolling = false,
  onOpenRunSettings,
  onRestoreToDefault,
  hasUnsavedChanges = false
}: ContestNavbarProps) {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('blue.600', 'blue.700');
  const textColor = useColorModeValue('white', 'white');
  const badgeColor = useColorModeValue('yellow.400', 'yellow.300');
  const badgeTextColor = useColorModeValue('gray.800', 'gray.900');

  if (!isContestProblem(problemId)) {
    return null;
  }

  const contestName = getContestProblemName(problemId);
  const contestShortName = getContestProblemShortName(problemId);
  const contestYear = getContestYear(problemId);
  const contestTags = getContestProblemTags(problemId);
  const leaderboardUrl = getContestLeaderboardUrl(problemId);

  return (
    <Box 
      bg={bgColor} 
      color={textColor} 
      px={4} 
      py={3} 
      borderBottom="1px" 
      borderColor="blue.500"
      position="sticky"
      top={0}
      zIndex={10}
    >
      <Flex justify="space-between" align="center">
        {/* Left side - Contest info and navigation */}
        <HStack spacing={4}>
          {/* Back button */}
          <Button
            variant="ghost"
            color={textColor}
            _hover={{ bg: 'blue.500' }}
            onClick={() => navigate('/')}
            size="sm"
            leftIcon={<ArrowBackIcon />}
          >
            Back to Problems
          </Button>

          {/* Contest branding */}
          <HStack spacing={2}>
            <Icon as={StarIcon} boxSize={5} />
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                {contestName}
              </Text>
              <Text fontSize="xs" opacity={0.8}>
                SPE4IC {contestYear} Contest
              </Text>
            </Box>
          </HStack>

          {/* Contest tags */}
          <HStack spacing={1}>
            {contestTags.slice(0, 2).map((tag) => (
              <Badge
                key={tag}
                colorScheme="yellow"
                variant="solid"
                size="sm"
                fontSize="xs"
              >
                {tag}
              </Badge>
            ))}
          </HStack>
        </HStack>

        {/* Right side - Actions */}
        <HStack spacing={3}>
          {/* Restore to Default Button */}
          {onRestoreToDefault && (
            <Button
              variant={hasUnsavedChanges ? "solid" : "ghost"}
              colorScheme={hasUnsavedChanges ? "orange" : "gray"}
              color={hasUnsavedChanges ? "white" : textColor}
              _hover={{ bg: hasUnsavedChanges ? 'orange.600' : 'blue.500' }}
              size="sm"
              leftIcon={<ArrowBackIcon />}
              onClick={onRestoreToDefault}
              isDisabled={!hasUnsavedChanges}
            >
              Restore
            </Button>
          )}
          
          {/* Run Settings button */}
          {onOpenRunSettings && (
            <Button
              variant="ghost"
              color={textColor}
              _hover={{ bg: 'blue.500' }}
              size="sm"
              leftIcon={<SettingsIcon />}
              onClick={onOpenRunSettings}
            >
              Run Settings
            </Button>
          )}

          {/* Contest info button */}
          <Button
            variant="ghost"
            color={textColor}
            _hover={{ bg: 'blue.500' }}
            size="sm"
            leftIcon={<InfoIcon />}
            onClick={() => {
              // Scroll to problem description
              const descriptionElement = document.getElementById('problem-description');
              if (descriptionElement) {
                descriptionElement.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Problem Info
          </Button>

          {/* Leaderboard button */}
          <ChakraLink as={RouterLink} to={leaderboardUrl} _hover={{ textDecoration: 'none' }}>
            <Button
              variant="ghost"
              color={textColor}
              _hover={{ bg: 'blue.500' }}
              size="sm"
              leftIcon={<ViewIcon />}
            >
              Leaderboard
            </Button>
          </ChakraLink>

          {/* Submit button */}
          {onSubmit && (
            <Button
              colorScheme="green"
              size="md"
              onClick={onSubmit}
              isLoading={isSubmitting || isPolling}
              loadingText={isPolling ? "Processing..." : "Submitting..."}
              fontWeight="bold"
              px={6}
            >
              Submit Solution
            </Button>
          )}
        </HStack>
      </Flex>

      {/* Contest status bar */}
      <Box mt={2} pt={2} borderTop="1px" borderColor="blue.500">
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <Text fontSize="sm" opacity={0.9}>
              Contest Problem: {contestShortName}
            </Text>
            {hasUnsavedChanges && (
              <Badge
                colorScheme="orange"
                variant="solid"
                size="sm"
                fontSize="xs"
              >
                Modified
              </Badge>
            )}
          </HStack>
          <Badge
            colorScheme="green"
            variant="solid"
            size="sm"
          >
            Active Contest
          </Badge>
        </Flex>
      </Box>
    </Box>
  );
} 